import { createSlice, PayloadAction, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { stat } from "fs";
import { Middleware, MiddlewareAPI, Dispatch } from "redux";
import { MenuOptions, ProjectItem } from "senac-components/dist/pages/Mobile/PageInterfaces";
import { AppDispatch, RootState } from "..";
import { UserService } from '../../services';
import { AuthSSOParams, ParamsSSO, User, UserRegisterData } from "../../services/user/IUserService";
import { commonSlice } from "./common";
import { Profile } from 'senac-components/dist/pages/Mobile/PageMyProfile';
import { ContactUsForm } from "senac-components/dist/pages/Mobile/PageContactUs";
import { userDataSlice } from "./userDataSlice";
import { string } from "yargs";
import { checkSimpleCpf, cpfValidator, sanitizeCpf } from "../../utilities/Cpf";
import { ERRORS } from "../../utilities/constants/errors";
import { contentSlice } from "./contentSlice";

interface UserState {
  projectsLoading: boolean;
  projectsAvailable?: ProjectItem[];
  activeProject?: ProjectItem;
  authorizationTokenRecovery?: string;
  recoveryStatus?: number;
  user?: User;
  userCopy?: User;
  userAuthenticating: boolean;
  userAuthenticationErro?: string;
  loadingSso?: boolean;
  userRegistering: boolean;
  openModalMigration: boolean;
  step?: number;
  login?: string;
  organizations?: { login: string, uid: string; name: string; migrated: boolean }[];
  changingPassword: boolean;
  updatingProfile: boolean;

  contact?: ContactUsForm;
  contactLoading: boolean;
  contactForm?: string;

  clearContactForm?: boolean;
  ssoParams?: Record<string, string>
  codeReadingList?: string;
  preferences?: any[];
  projects?: any[];
  updatingUserError?: string;
  signinSsoError?: { code: string, message: string };
  ssoData?: ParamsSSO;
  lastCpfSetted?: string;
  lastLoginSetted?: string;
}

const initialState: UserState =
{
  projectsLoading: false,
  userAuthenticating: false,
  contactLoading: false,
  userRegistering: false,
  changingPassword: false,
  updatingProfile: false,
  openModalMigration: false,
};

interface LoginProps {
  login: string,
  password: string
}

const projectsAvailable = createAsyncThunk("users/projectsavailable", (login: string) => UserService.ProjectsAvailable(login));

const userLogout = createAsyncThunk("users/logout", () => UserService.Logout());

const authenticateUser = createAsyncThunk("users/authenticate", (props: LoginProps & { ignoreActive?: boolean }) => UserService.SigninDefault(props.login, props.password, props.ignoreActive));

const restoreUser = createAsyncThunk("users/restore", () => UserService.ActiveUser());

const errorNoOrganizations = createAction('/user/organizations/empty');

const recoverPassword = createAsyncThunk("users/forgot/password", (login: string) => UserService.RecoverPassword(login));

const contact = createAsyncThunk("users/contact", (contact: ContactUsForm) => UserService.SentContactUs(contact));

const signinAfterAuth = createAsyncThunk("users/signin/after/auth", (params: any) => UserService.SigninSsoAfterAuthentication(params));

const remoteRegisterUser = createAsyncThunk("users/forgot/password", (data: UserRegisterData) => UserService.RegisterUser(data));

const organizationsFromLogin = createAsyncThunk("users/organizations/from/login", (login: string) => UserService.ProjectsFromLogin(login))

const activeUserFromSso = createAction<Record<string, any>>("user/active/from/sso");

const showAlertSSO = createAction("user/alert/sso");

export const authenticateFromProfileContext = createAction<string>("user/signin/from/profile-context/")

export const updatingUser = createAction<Profile>('user/updating/profile')

export const checkPendingCpfOrEmail = createAction('user/checking/required/values')

export const checkUniqueLogin = createAction('user/checking/login')

export const sendCPFEmail = createAction<string>('/user/update/required/values');

export const sendNewLogin = createAction<string>('/user/update/login');

export const signinFromProfile = createAction<LoginProps>('/user/signin/profile');

export const authenticateSso = createAction<ParamsSSO>("user/sso/auth");

export const authenticateSsoFromProvision = createAction<AuthSSOParams>("user/sso/v3/auth");

export const forgotPasswordFromLibrary = createAction<{ login: string, organization: string }>("user/forgot/password/from/library");

export const onCloseMigrationModal = createAction('/user/migration/modal/close');

export const updatedProfile = createAsyncThunk('user/updated/profile', (data: Profile) => UserService.UpdatedProfile(data))

export const changePassword = createAsyncThunk("users/change/password", (passwordData: Record<string, string>) => UserService.ChangePassword(passwordData.password, passwordData.currentPassword));

export const createPassowrd = createAsyncThunk("user/create/password", (passwordData: Record<string, string>) => UserService.CreatePassword(passwordData.token, passwordData.password));

export const signinAnonymous = createAsyncThunk("user/anonymous/signin", (projectUid: string) => UserService.AnonymousSignIn(projectUid))

export const signinSso = createAsyncThunk("user/sso/signin/from/external-context", (params: ParamsSSO) => UserService.AuthenticateSSO(params))

export const signinSSOFromProvision = createAsyncThunk("user/sso/signin/from/provision", (params: AuthSSOParams) => UserService.AuthenticateSSOFromProvision(params))

export const signinMigration = createAsyncThunk("user/authenticate/migration", (props: LoginProps) => UserService.SigninMigrationFlow(props.login, props.password))

export const sendOrganization = createAsyncThunk("user/migration/organization", (params: { login: string, password: string, organizationUid: string }) => UserService.SendOrganization(params.login, params.password, params.organizationUid))

export const onCreatePreference = createAsyncThunk("user/preference/new", (preference: Record<string, any>) => UserService.CretePreference(preference))

export const onForgotPasswordLibrary = createAsyncThunk("user/forgot/password/library", (params: { login: string, organization: string }) => UserService.SendForgotPasswordLibrary(params))

export const userSlice = createSlice({
  name: "userSlice",
  initialState,
  reducers: {
    resetState: () => initialState,

    setLastLogin: (state, action) => {
      state.lastLoginSetted = action.payload
    },
    setLastCpf: (state, action) => {
      state.lastCpfSetted = action.payload
    },
    setTokenRecoveryPassword: (state, action) => {
      state.authorizationTokenRecovery = action.payload
    },

    setSsoLoading: (state, action) => {
      state.loadingSso = action.payload
    },

    setSsoData: (state, action) => {
      state.ssoData = action.payload
    },

    setUserWithDetails: (state, action) => {
      const result = action.payload;

      if (!result || !result.user) return;

      state.user = result.user;

      if (result.preferences) {
        state.preferences = result.preferences
      }
      if (result.projects) {
        state.projects = result.projects
      }
    },

    setPreferences: (state, action) => {
      state.preferences = action.payload;
    },

    setProjects: (state, action) => {
      state.projects = action.payload;
    },

    showAlertRecoveryPassword: (state, action: PayloadAction<number>) => {
      state.recoveryStatus = action.payload;
    },
    clearForm: (state, data) => {
      state.clearContactForm = data.payload
    },

    checkAvailableProjects: (state, login: PayloadAction<string>) => {
      const isCpf = checkSimpleCpf(login.payload);
      state.login = isCpf ? login.payload.replaceAll('.', '').replace('-', '') : login.payload;
      state.projectsAvailable = undefined;
      state.lastCpfSetted = undefined;
    },
    selectProject: (state, project: PayloadAction<ProjectItem>) => {
      state.activeProject = project.payload;
    },
    signin: (state, password: PayloadAction<string>) => {
      state.user = undefined;
    },
    signinAfterPayload: (state, data) => {
      state.loadingSso = true;
    },
    syncOrganization: (state) => {
      state.openModalMigration = !state.openModalMigration;
      state.step = -1;
    },
    showMyProjects: (state) => {
      state.openModalMigration = true;
      state.step = 4;
      state.organizations = (state.projects ?? []).map(item => ({
        uid: item.uid,
        name: item.name,
        migrated: true,
        login: ''
      }))
    },
    closeModal: (state) => {
      state.openModalMigration = false;
    },
    toggleModalMigration: (state) => {
      state.openModalMigration = !state.openModalMigration
      state.step = 0;
    },
    setMigrated: (state, data) => {
      const newOrganizations = (state.organizations ?? []).map(item => {
        if (item.uid === data.payload.organizationUid && item.login === data.payload.login) return {
          ...item,
          migrated: true,
        }
        return item;
      });

      state.organizations = newOrganizations
    },

    sendUserOrganization: (state, data: PayloadAction<any>) => {
      state.login = data.payload.login;
    },

    changeStep: (state, data) => {
      state.step = data.payload;
    },

    setOrganizations: (state, data) => {
      const organizations = data.payload ?? [];
      state.organizations = organizations;

      if (organizations.length) {
        state.openModalMigration = true;
      }
      else {
        state.openModalMigration = false;
      }
    },

    activeUser: (state, data: PayloadAction<User | undefined>) => {
      state.user = data.payload;
      state.ssoParams = undefined;
    },
    activeUserCopy: (state, data: PayloadAction<User | undefined>) => {
      state.userCopy = data.payload;
      state.ssoParams = undefined;
    },
    logout: (state) => {
      state.user = undefined;
    },
    registerUser: (state, data: PayloadAction<UserRegisterData>) => {
      state.user = undefined;
      state.login = data.payload.login;
    },
    recoverPassword: (state, data: PayloadAction<string>) => {
      state.login = data.payload;
    },
    contactUs: (state, data: PayloadAction<ContactUsForm>) => {
      state.contact = undefined;
    },
    createPasswordFromRecovery: (state, data) => {
      state.changingPassword = true
    },
    checkSigninSSO: (state, data) => {
      state.ssoParams = data.payload
    },
    setImportReadingList: (state, data) => {
      state.codeReadingList = data.payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(onForgotPasswordLibrary.pending, (state) => {
      state.updatingProfile = true;
      state.userAuthenticating = true;
    })
    builder.addCase(onForgotPasswordLibrary.rejected, (state) => {
      state.updatingProfile = false;
      state.userAuthenticating = false;
    })
    builder.addCase(onForgotPasswordLibrary.fulfilled, (state) => {
      state.updatingProfile = false;
      state.userAuthenticating = false;
    })
    builder.addCase(signinSSOFromProvision.pending, (state) => {
      state.loadingSso = true
    })
    builder.addCase(signinSSOFromProvision.rejected, (state) => {
      state.loadingSso = false
    })
    builder.addCase(signinSSOFromProvision.fulfilled, (state) => {
      state.loadingSso = false
    })
    builder.addCase(contact.pending, (state) => {
      state.contactLoading = true;
    });

    builder.addCase(contact.fulfilled, (state, action) => {
      state.contactLoading = false;
      state.contact = action.payload;
    });

    builder.addCase(projectsAvailable.pending, (state, action) => {
      state.projectsLoading = true;
    });

    builder.addCase(projectsAvailable.fulfilled, (state, action) => {
      state.projectsLoading = false;
      state.projectsAvailable = action.payload;

      if (state.projectsAvailable?.length == 1) {
        state.activeProject = state.projectsAvailable[0];
      }

    });

    builder.addCase(signinMigration.pending, (state, action) => {
      state.userAuthenticating = true;
    });

    builder.addCase(signinMigration.fulfilled, (state, action) => {
      state.userAuthenticating = false;
    });

    builder.addCase(signinMigration.rejected, (state, action) => {
      state.userAuthenticating = false;

      if (action.error.code == "401") {
        state.userAuthenticationErro = "Login ou senha inválido."
      }
      else {
        state.userAuthenticationErro = "Falha ao autenticar usuário."
      }
    });

    builder.addCase(sendOrganization.pending, (state, action) => {
      state.userAuthenticating = true;
    });

    builder.addCase(sendOrganization.fulfilled, (state, action) => {
      state.userAuthenticating = false;
    });

    builder.addCase(sendOrganization.rejected, (state, action) => {
      state.userAuthenticating = false;
    });

    builder.addCase(restoreUser.fulfilled, (state, action) => {
      state.userAuthenticating = false;
    });

    builder.addCase(remoteRegisterUser.pending, (state, action) => {
      state.userRegistering = true;
    });

    builder.addCase(remoteRegisterUser.fulfilled, (state, action) => {
      state.userRegistering = false;
    })

    builder.addCase(remoteRegisterUser.rejected, (state, action) => {
      state.userRegistering = false;
    })
    builder.addCase(signinAfterAuth.pending, (state, action) => {
      state.userRegistering = true;
      state.userAuthenticating = true;
    });

    builder.addCase(signinAfterAuth.fulfilled, (state, action) => {
      state.userRegistering = false;
      state.userAuthenticating = false;
    })

    builder.addCase(signinAfterAuth.rejected, (state, action) => {
      state.userRegistering = false;
      state.userAuthenticating = false;
    })

    builder.addCase(changePassword.pending, (state, action) => {
      state.changingPassword = true
    })

    builder.addCase(changePassword.fulfilled, (state, action) => {
      state.changingPassword = false
    })

    builder.addCase(updatedProfile.pending, (state, action) => {
      state.updatingProfile = true
      state.userAuthenticating = true
    })

    builder.addCase(updatedProfile.fulfilled, (state, action) => {
      state.updatingProfile = false
      state.userAuthenticating = false
    })
    builder.addCase(updatedProfile.rejected, (state, action) => {
      state.updatingProfile = false
      state.userAuthenticating = false

      if (action.error.message) {
        state.updatingUserError = action.error.message;
      }
    })
    builder.addCase(authenticateUser.pending, (state, action) => {
      state.updatingProfile = true
      state.userAuthenticating = true
    })

    builder.addCase(authenticateUser.fulfilled, (state, action) => {
      state.updatingProfile = false
      state.userAuthenticating = false
    })

    builder.addCase(authenticateUser.rejected, (state, action) => {
      state.updatingProfile = false
      state.userAuthenticating = false
    })

    builder.addCase(signinSso.pending, (state, action) => {
      state.loadingSso = true;
    })

    builder.addCase(signinSso.rejected, (state, action) => {
      state.loadingSso = false;

      if (action.error.message) {
        if (action.error.message === '400') {
          state.signinSsoError = { code: '400', message: "Ocorreu um erro interno, por favor tente novamente." }
          state.ssoData = undefined;
        }
        if (action.error.message === '403') {
          state.signinSsoError = { code: '403', message: "Estamos atualizando a plataforma e para oferecer uma melhor experiência. Por favor, insira suas credenciais de acesso ou realize o cadastro para uma nova conta." }
        }
        if (action.error.message === '401') {
          state.signinSsoError = { code: '401', message: 'Falha ao autenticar.' }
          state.ssoData = undefined;
        }
      }
    })
    builder.addCase(signinSso.fulfilled, (state, action) => {
      state.loadingSso = false;
    })
  },
});

const checkContactUsForm = (form: ContactUsForm) => {
  const requiredFields = ['name', 'email', 'message', 'assunto'];
  const shouldSendForm = requiredFields.every(field => {
    const propertyForm = form[field as keyof typeof form];
    return propertyForm && propertyForm.length > 0;
  })

  if (!shouldSendForm) return false;

  return true;
};

export const middleware: Middleware = (store: MiddlewareAPI<AppDispatch, RootState>) =>
  (next: Dispatch) =>
    async (action: PayloadAction<any>) => {
      if (commonSlice.actions.changeState.match(action) && action.payload == "initializing") {
        store.dispatch(restoreUser())
          .unwrap()
          .then(user => {
            store.dispatch(userSlice.actions.activeUser(user));
            // store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home))
          });
      }
      else if (authenticateFromProfileContext.match(action)) {
        const state = store.getState();
        const login = state.userState.lastCpfSetted;
        const lastLogin = state.userState.lastLoginSetted;

        if (!login) return;

        store.dispatch(authenticateUser({ login: sanitizeCpf(login), password: action.payload, ignoreActive: true }))
          .unwrap()
          .then(result => {
            store.dispatch(signinFromProfile({ password: "", login: lastLogin ?? "" }))
            store.dispatch(userSlice.actions.setUserWithDetails(result))
            store.dispatch(userSlice.actions.changeStep(0));
            store.dispatch(restoreUser())
          })
          .catch(err => {
            store.dispatch(commonSlice.actions.showAlert({
              title: "Meus dados",
              description: ERRORS.GENERIC_ERROR,
              show: true
            }));
          })
      }
      else if (userSlice.actions.setSsoData.match(action)) {
        store.dispatch(userSlice.actions.setSsoLoading(Boolean(action.payload)))
      }
      else if (forgotPasswordFromLibrary.match(action)) {
        const state = store.getState();
        let fn: any = () => {
          return onForgotPasswordLibrary(action.payload);
        }
        if (state.userState.lastCpfSetted && state.userState.step !== 1) {
          fn = () => {
            return recoverPassword(sanitizeCpf(state.userState.lastCpfSetted ?? ''));
          }
        };

        store.dispatch(fn()).unwrap()
          .then((result: any) => {
            if (result) {
              store.dispatch(commonSlice.actions.showAlert({
                title: "Recuperação de senha",
                description: `Siga as instruções da mensagem enviada para o e-mail ${result?.email ?? 'cadastrado'}`,
                show: true
              }));
            }
            else {
              store.dispatch(commonSlice.actions.showAlert({
                title: "Recuperação de senha",
                description: 'Falha ao solicitar alteração de senha, tente novamente mais tarde',
                show: true
              }));
            }
            store.dispatch(userSlice.actions.changeStep(0));
            store.dispatch(userSlice.actions.closeModal());
          })
          .catch(() => {
            store.dispatch(commonSlice.actions.showAlert({
              title: "Recuperação de senha",
              description: 'Falha ao solicitar alteração de senha, tente novamente mais tarde',
              show: true
            }));
            store.dispatch(userSlice.actions.changeStep(0));
            store.dispatch(userSlice.actions.closeModal());
          })

      }
      else if (activeUserFromSso.match(action)) {
        const result = action.payload;

        store.dispatch(commonSlice.actions.changeState('initialized'));
        store.dispatch(userSlice.actions.setUserWithDetails(result));
        store.dispatch(userSlice.actions.setSsoData(undefined));
        store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home))
      }
      else if (userSlice.actions.signinAfterPayload.match(action)) {
        store.dispatch(signinAfterAuth(action.payload)).unwrap().then(res => {
          store.dispatch(activeUserFromSso(res));
          store.dispatch(userSlice.actions.setSsoData(undefined));
        })
          .catch(err => {
            store.dispatch(commonSlice.actions.showAlert({
              title: 'Registro',
              description: 'Ocorreu um erro ao tentar fazer login.',
              show: true,
            }))
          })

      }

      else if (authenticateSso.match(action)) {
        const state = store.getState();
        store.dispatch(userSlice.actions.setSsoData(action.payload));
        store.dispatch(userLogout()).then(() => {
          store.dispatch(signinSso(action.payload)).unwrap()
            .then((result) => {
              store.dispatch(commonSlice.actions.changeState('initialized'));
              store.dispatch(userSlice.actions.activeUser(result.user));
              store.dispatch(userSlice.actions.setSsoData(undefined));
              if (result.preferences) {
                store.dispatch(userSlice.actions.setPreferences(result.preferences))
              }
              if (result.projects) {
                store.dispatch(userSlice.actions.setProjects(result.projects))
              }

              if (action.payload.path) {
                const hashPath = `#${action.payload.path.startsWith("/") ? action.payload.path : "/" + action.payload.path}`;
                window.location.hash = hashPath;
              } else {
                store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home));
              }
            })
            .catch(err => {
              const { code, message } = state.userState.signinSsoError ?? {};

              if (code === '401' || code === '403') {
                store.dispatch(commonSlice.actions.loginOptionsPage())
              };

              store.dispatch(showAlertSSO());

            })
        })

      }
      else if (authenticateSsoFromProvision.match(action)) {
        const state = store.getState();
        store.dispatch(userSlice.actions.setSsoData(action.payload));
        store.dispatch(userLogout()).then(() => {
          store.dispatch(signinSSOFromProvision(action.payload)).unwrap()
            .then((result) => {
              store.dispatch(commonSlice.actions.changeState('initialized'));
              store.dispatch(userSlice.actions.activeUser(result));
              store.dispatch(userSlice.actions.setSsoData(undefined));

              if (action.payload.path) {
                const hashPath = `#${action.payload.path.startsWith("/") ? action.payload.path : "/" + action.payload.path}`;
                window.location.hash = hashPath;
              } else {
                store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home));
              }
            })
            .catch(err => {
              const { code, message } = state.userState.signinSsoError ?? {};

              if (code === '401' || code === '403') {
                store.dispatch(commonSlice.actions.loginOptionsPage())
              };

              store.dispatch(showAlertSSO());
            })
        })
      }

      else if (showAlertSSO.match(action)) {
        const state = store.getState();
        const { code, message } = state.userState.signinSsoError ?? {};
        store.dispatch(commonSlice.actions.showAlert({
          description: message ?? "Ocorreu um erro de autenticação. Tente novamente mais tarde.",
          show: true,
          title: 'Autenticação',
        }));
      }
      else if (errorNoOrganizations.match(action)) {
        store.dispatch(commonSlice.actions.showAlert({
          description: "Não encontramos nenhuma organização vinculada ao usuário.",
          show: true,
          title: 'Nenhuma organização',
        }))

        store.dispatch(userSlice.actions.closeModal())
      }
      else if (userSlice.actions.setOrganizations.match(action)) {
        const organizations = action.payload;

        if (!organizations || !organizations.length) {
          store.dispatch(errorNoOrganizations())
        }
      }
      else if (onCloseMigrationModal.match(action)) {
        store.dispatch(userSlice.actions.toggleModalMigration())

        if (!window.location.hash.includes('profile')) { // TODO: Melhorar verificação
          store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home));
        }
      }
      else if (checkUniqueLogin.match(action)) {
        const state = store.getState();
        const user = state.userState.user;

        if (user) {
          store.dispatch(
            userSlice.actions.closeModal()
          )

          if (!user.cpf || !user.email || user.login !== user.cpf) {
            store.dispatch(
              commonSlice.actions.goToPage(MenuOptions.Profile)
            )

            store.dispatch(
              commonSlice.actions.showAlert({
                title: "Atualização de dados",
                description: "Estamos atualizando a plataforma para oferecer uma melhor experiência. A partir de agora, o login será substituído pelo CPF. Por favor, verifique e, se necessário, atualize as informações: CPF e e-mail.",
                show: true
              })
            )

            return;
          };

          if (!window.location.hash.includes('profile')) {
            commonSlice.actions.goToPage(MenuOptions.Home)
            window.location.hash = '#/inicio'
          }
        }
      }

      else if (checkPendingCpfOrEmail.match(action)) {
        const state = store.getState();
        const user = state.userState.userCopy;

        if (user) {
          if (user.cpf || user.email) {
            store.dispatch(userSlice.actions.toggleModalMigration());
            store.dispatch(userSlice.actions.activeUser(user));
            return;
          }
          store.dispatch(userSlice.actions.changeStep(3));
        }
      }
      else if (userSlice.actions.sendUserOrganization.match(action)) {
        const errorLink = () => {
          store.dispatch(
            commonSlice.actions.showAlert({
              title: "Login",
              description: "Falha ao autenticar usuário.",
              show: true
            })
          )
        }
        store.dispatch(sendOrganization(action.payload)).unwrap().then((result: any) => {
          const state = store.getState();
          if (result) {
            store.dispatch(commonSlice.actions.showAlert({
              description: "Organização vinculada com sucesso!",
              show: true,
              title: "Organização"
            }))
            store.dispatch(userSlice.actions.setMigrated(action.payload))
            store.dispatch(userSlice.actions.activeUserCopy(result.user));
            store.dispatch(userSlice.actions.activeUser(result.user));

            if (result.preferences) {
              store.dispatch(userSlice.actions.setPreferences(result.preferences))
            }
            if (result.projects) {
              store.dispatch(userSlice.actions.setProjects(result.projects))
            }
            store.dispatch(userSlice.actions.changeStep(0));

            if (state.userState.organizations?.length === 1) {
              store.dispatch(userSlice.actions.closeModal())
              store.dispatch(userSlice.actions.setLastCpf(undefined))
              store.dispatch(userSlice.actions.setLastLogin(undefined))
              store.dispatch(restoreUser())
            }

            return;
          }

          errorLink();
        })
          .catch(err => {
            console.log(err);
            errorLink();
          })
      }

      else if (userSlice.actions.checkSigninSSO.match(action)) {
        const params = action.payload;

        if (!params) return;

        const requiredParams = ['state', 'connector'];
        const nextSSO = requiredParams.every(param => Boolean(params[param]));

        if (!nextSSO) return;

        store.dispatch(signinSSOFromProvision(action.payload)).unwrap().then((result) => {
          store.dispatch(userSlice.actions.activeUser(result))
        })
      }
      else if (userSlice.actions.showAlertRecoveryPassword.match(action)) {
        const recoveryMessages: Record<number, string> = {
          400: "O link para cadastrar uma nova senha expirou. Tente executar a solicitação novamente.",
          200: "Nova senha cadastrada com sucesso. Você será redirecionado para a tela de login.",
          500: "Ocorreu um erro interno",
          404: "Ocorreu um erro, tente novamente mais tarde"
        };

        const description = recoveryMessages[action.payload ?? 500];
        const baseAlert = { title: "Recuperação de senha", show: true };
        store.dispatch(
          commonSlice.actions.showAlert({
            ...baseAlert,
            description,
          })
        );
        setTimeout(() => {
          if (action.payload === 200) {
            window.location.hash = '/user/login'
          }
        }, 2000)
      }
      else if (userSlice.actions.createPasswordFromRecovery.match(action)) {
        const state = store.getState();
        store
          .dispatch(
            createPassowrd({
              token: state.userState.authorizationTokenRecovery ?? "",
              password: action.payload?.newPassword,
            })
          )
          .unwrap()
          .then((result) => {
            store.dispatch(userSlice.actions.showAlertRecoveryPassword(result))
          })
          .catch(err => {
            store.dispatch(userSlice.actions.showAlertRecoveryPassword(err))
          })
      }

      else if (userSlice.actions.contactUs.match(action)) {
        store.dispatch(userSlice.actions.clearForm(false))

        const correctForm = checkContactUsForm(action.payload);

        if (!correctForm) {
          store.dispatch(commonSlice.actions.showAlert({
            title: "Fale Conosco",
            description: "Preencha todos os campos obrigatórios para submeter o contato.",
            show: true
          }))
          return;
        };

        store.dispatch(contact(action.payload)).unwrap()
          .then(result => {
            if (result) {
              store.dispatch(commonSlice.actions.showAlert({
                title: "Fale Conosco",
                description: "Contato enviado",
                show: true
              }))

              store.dispatch(userSlice.actions.clearForm(true))
            }
            else {
              store.dispatch(commonSlice.actions.showAlert({
                title: "Fale Conosco",
                description: "Ocorreu ao tentar enviar os dados. Tente novamente mais tarde",
                show: true
              }))
            }

            return result;
          })
          .catch(() => {
            store.dispatch(commonSlice.actions.showAlert({
              title: "Fale Conosco",
              description: "Ocorreu ao tentar enviar os dados. Tente novamente mais tarde",
              show: true
            }))
          })
      }
      else if (userSlice.actions.checkAvailableProjects.match(action)) {
        store.dispatch(commonSlice.actions.loginPasswordPage());
      }
      else if (userSlice.actions.selectProject.match(action)) {
        store.dispatch(commonSlice.actions.loginPasswordPage());
      }

      else if (userSlice.actions.signin.match(action)) {
        var state = store.getState();
        var login = state.userState.login;
        var password = action.payload;

        if (login && password) {
          const errorLogin = () => {
            state = store.getState()
            store.dispatch(
              commonSlice.actions.showAlert({
                title: "Login",
                description: state.userState.userAuthenticationErro ?? "Erro",
                show: true
              })
            )
          }
          if (state.userState.ssoData) {
            store.dispatch(userSlice.actions.signinAfterPayload({
              ...state.userState.ssoData,
              login,
              password,
              initialLogin: state.userState.ssoData.login,
            }))
            store.dispatch(userSlice.actions.setSsoData(undefined));
            return;
          }

          var props: LoginProps = { login, password };
          store.dispatch(signinMigration(props))
            .unwrap()
            .then((res) => {
              if (!res?.user) {
                errorLogin();
                return;
              }

              if (res.preferences) {
                store.dispatch(userSlice.actions.setPreferences(res.preferences));
              }

              if (res.projects) {
                store.dispatch(userSlice.actions.setProjects(res.projects));
              }

              store.dispatch(userSlice.actions.activeUser(res.user));

              if (res.organization && res.firstLogin) {
                store.dispatch(userSlice.actions.setOrganizations(res.organizations))
                return;
              };

              store.dispatch(checkUniqueLogin());
            })
            .catch((err) => {
              console.log(err)
              errorLogin();
            });
        }
      }
      else if (signinFromProfile.match(action)) {
        const { login } = action.payload

        store.dispatch(organizationsFromLogin(login)).unwrap()
          .then(res => {
            if (res.organizations) {
              store.dispatch(userSlice.actions.setOrganizations(res.organizations))
              store.dispatch(userSlice.actions.changeStep(0));
            }
          })
          .catch(err => {
            store.dispatch(
              commonSlice.actions.showAlert({
                title: "Login",
                description: "Erro ao tentar obter as organizações vinculadas.",
                show: true
              })
            )
          })
      }
      else if (userSlice.actions.logout.match(action)) {
        store.dispatch(commonSlice.actions.resetState())
        store.dispatch(userSlice.actions.resetState())
        store.dispatch(userDataSlice.actions.resetState())
        store.dispatch(contentSlice.actions.resetState())
        store.dispatch(userLogout());
        store.dispatch(commonSlice.actions.loginOptionsPage());
      }
      else if (userSlice.actions.activeUser.match(action)) {
        var state = store.getState();
        if (action.payload && state.userState.user?.uid !== action.payload.uid) {
          // store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home));
        }
      }
      else if (userSlice.actions.recoverPassword.match(action)) {
        store.dispatch(recoverPassword(action.payload)).unwrap().then((result: any) => {
          store.dispatch(commonSlice.actions.showAlert({
            title: "Recuperação de senha",
            description: `Siga as instruções da mensagem enviada para o e-mail ${result?.email ?? 'cadastrado'}`,
            show: true
          }));

          store.dispatch(commonSlice.actions.loginPage());
        })
          .catch(() => {
            store.dispatch(commonSlice.actions.showAlert({
              title: "Recuperação de senha",
              description: 'Falha ao solicitar alteração de senha, tente novamente mais tarde',
              show: true
            }));
          })
      }
      else if (userSlice.actions.registerUser.match(action)) {
        const state = store.getState();
        store.dispatch(remoteRegisterUser(action.payload))
          .then((e) => {
            var message = "Erro ao cadastrar usuário.";
            switch (e.meta.requestStatus) {
              case "fulfilled":
                {
                  if (state.userState.ssoData) {
                    store.dispatch(userSlice.actions.signinAfterPayload({
                      ...state.userState.ssoData,
                      login: action.payload.cpf,
                      password: action.payload.password,
                      initialLogin: state.userState.ssoData.login
                    }))
                    store.dispatch(userSlice.actions.setSsoData(undefined));
                  }
                  else {
                    message = "Cadastro feito com sucesso";
                    store.dispatch(commonSlice.actions.showAlert({
                      title: "Cadastro",
                      description: "Cadastro feito com sucesso.",
                      show: true
                    }));
                    store.dispatch(commonSlice.actions.loginPage());
                  }
                  break;
                }
              case "rejected":
                {
                  message = (e as any)?.error?.message ?? message
                  store.dispatch(commonSlice.actions.showAlert({
                    title: "Cadastro",
                    description: message,
                    show: true
                  }));
                }
            }
          });
      }
      else if (changePassword.fulfilled.match(action)) {
        store.dispatch(commonSlice.actions.backPage());
        store.dispatch(commonSlice.actions.showAlert({
          title: "Login",
          description: "Senha alterada com sucesso.",
          show: true
        }));
      }
      else if (changePassword.rejected.match(action)) {
        store.dispatch(commonSlice.actions.showAlert({
          title: "Login",
          description: "Não foi possível alterar sua senha",
          show: true
        }));
      }
      else if (sendCPFEmail.match(action)) {
        const state = store.getState();
        const user = state.userState.userCopy
        if (user) {
          store.dispatch(updatedProfile({ ...user, cpf: action.payload })).unwrap().then((profile) => {

            var state = store.getState();
            var user = state.userState.user;

            if (!user) return;

            var userUpdated = { ...user, ...profile };
            store.dispatch(userSlice.actions.activeUser(userUpdated));
            store.dispatch(userSlice.actions.toggleModalMigration());
            store.dispatch(commonSlice.actions.goToPage(MenuOptions.Home));
          })
        }
      }
      else if (sendNewLogin.match(action)) {
        const state = store.getState();
        const user = state.userState.userCopy
        if (user) {
          store.dispatch(updatedProfile({ ...user, login: action.payload })).unwrap().then((profile) => {

            var state = store.getState();
            var user = state.userState.user;

            if (!user) return;

            var userUpdated = { ...user, ...profile };
            store.dispatch(userSlice.actions.activeUser(userUpdated));
            store.dispatch(checkPendingCpfOrEmail());
          })
        }
      }
      else if (updatingUser.match(action)) {
        store.dispatch(userSlice.actions.setLastCpf(action.payload.cpf));
        const state = store.getState();
        store.dispatch(userSlice.actions.setLastLogin(state.userState.login))
        const errorUpdated = () => {
          store.dispatch(
            commonSlice.actions.showAlert({
              title: "Erro ao atualizar usuário",
              description: state.userState.updatingUserError ?? "Não foi possível atualizar os dados.",
              show: true
            })
          )
        };

        store.dispatch(updatedProfile(action.payload)).unwrap().then((profile) => {
          if (!profile) {
            errorUpdated();
            return;
          }

          var user = state.userState.user;

          if (!user) return;

          const preferences = state.userState.preferences ?? [];

          const acceptedTerms = preferences.find((item) => item.key === 'accepted_privacy_police');

          if (!acceptedTerms) {
            store.dispatch(onCreatePreference({ key: 'accepted_privacy_police', value: true }))
              .unwrap()
              .then(res => {
                if (res) {
                  store.dispatch(userSlice.actions.setPreferences(
                    [...preferences, res]
                  ))
                }
              })
              .catch(err => {
                console.log(err);
              })
          };

          var userUpdated = { ...user, ...profile };

          store.dispatch(commonSlice.actions.showAlert({
            show: true,
            title: "Meus dados",
            description: "Dados atualizados com sucesso!"
          }))
          store.dispatch(userSlice.actions.activeUser(userUpdated));
        }).catch((err) => {
          if (err && err.message === ERRORS.USER_UPDATING_ERROR_BECAUSE_CPF_ALREADY_REGISTERED) {
            store.dispatch(
              commonSlice.actions.showAlert({
                title: "Meus dados",
                description: ERRORS.USER_UPDATING_ERROR_BECAUSE_CPF_ALREADY_REGISTERED,
                show: true,
                buttons: [
                  {
                    callback: () => {
                      store.dispatch(commonSlice.actions.showAlert({ show: false, title: "", description: "" }))
                      store.dispatch(userSlice.actions.toggleModalMigration())
                      store.dispatch(userSlice.actions.changeStep(5))
                    },
                    label: "Login",
                    styles: {
                      background: "#084c8a",
                      color: "#fff",
                      margin: "auto",
                    }
                  }
                ]
              })
            )
            return;
          }
          errorUpdated();
        })
      }

      return next(action);
    };

export default userSlice.reducer;
